<template>
  <div>
    <DataTable
      class="p-datatable-sm"
      responsiveLayout="stack"
      breakpoint="60rem"
      :stripedRows="true"
      :loading="stateLoading"
      :value="linhas"
    >
      <template #empty>
        <div class="p-text-center p-p-2">Nenhuma linha nas filas do RNTRC.</div>
      </template>
      <Column header="Fila" style="width: 9rem">
        <template #body="{ data }">
          <span
            class="aet-badge"
            :class="
              data.fila === 'MON' ? 'aet-badge--warning' : 'aet-badge--info'
            "
            v-text="getNomeFila(data.fila)"
          />
          <i
            v-if="data.prioridade"
            class="fas fa-bolt p-ml-1 rntrc-prioridade"
            v-title="'Prioridade (pagamento informado)'"
          />
        </template>
      </Column>
      <Column header="Solicitação">
        <template #body="{ data }">
          <router-link
            v-if="data.idRntrcSolicitacao"
            :to="{
              name: 'rntrc-solicitacao-detalhe',
              params: { idRntrcSolicitacao: data.idRntrcSolicitacao + '' }
            }"
            v-text="'nº ' + data.idRntrcSolicitacao"
          />
          <div>
            <small>
              {{ getTipoServicoLabel(data.flTipoServico) }}
              <span v-if="data.cpfCnpj">
                · {{ formatCpfCnpj(data.cpfCnpj) }}</span
              >
            </small>
          </div>
          <div v-if="data.nomeCliente">
            <small class="rntrc-muted" v-text="data.nomeCliente" />
          </div>
        </template>
      </Column>
      <Column header="Situação">
        <template #body="{ data }">
          <span
            v-if="data.flSituacao"
            class="aet-badge"
            :class="'aet-badge--' + getSituacaoCor(data.flSituacao)"
            v-text="getSituacaoLabel(data.flSituacao)"
          />
          <div v-if="data.ultimaSituacaoSite">
            <small class="rntrc-muted"
              >site: {{ data.ultimaSituacaoSite }}</small
            >
          </div>
        </template>
      </Column>
      <Column header="Status robô">
        <template #body="{ data }">
          {{ data.statusRobo }}
          <div v-if="data.nomeRobo">
            <small class="rntrc-muted" v-text="data.nomeRobo" />
          </div>
        </template>
      </Column>
      <Column field="tentativas" header="Tentativas" style="width: 6rem" />
      <Column header="Última / próxima execução" style="width: 12rem">
        <template #body="{ data }">
          <div>
            <small
              >última:
              {{ formatDateTime(data.timestampUltimaExecucao) || "---" }}</small
            >
          </div>
          <div>
            <small
              >próxima:
              {{
                formatDateTime(data.timestampProximaExecucao) || "---"
              }}</small
            >
          </div>
        </template>
      </Column>
      <Column header="Erro">
        <template #body="{ data }">
          <span
            v-if="data.logErro"
            class="rntrc-log"
            v-tooltip.top="data.logErro"
            v-text="resumir(data.logErro)"
          />
        </template>
      </Column>
      <Column style="width: 7rem; text-align: right">
        <template #body="{ data }">
          <button-group>
            <Button
              icon="fas fa-redo"
              class="p-button-rounded"
              v-title="'Reprocessar'"
              @click="reprocessar(data)"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-rounded p-button-danger"
              v-title="'Remover da fila'"
              @click="remover(data)"
            />
          </button-group>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref
} from "vue";
import { formatDateTime } from "../../modules/utils";
import { useConfirm, useToast } from "../../modules/message";
import { useRntrcFilaService } from "../../modules/business/rntrc/rntrcFilaBusiness";
import {
  RntrcFilaPainelPayload,
  formatCpfCnpj,
  getSituacaoCor,
  getSituacaoLabel,
  getTipoServicoLabel
} from "../../modules/business/rntrc/rntrcTypes";

interface FilaRntrcPainelState {
  linhas: Array<RntrcFilaPainelPayload>;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup() {
    const state = reactive<FilaRntrcPainelState>({
      linhas: [],
      loadings: []
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const submitSearch = () => {
      const { data, loading, listar } = useRntrcFilaService();
      state.loadings.push(loading);
      return listar()
        .then(() => {
          state.linhas = Array.isArray(data.value) ? data.value : [];
          removeLoading(loading);
        })
        .catch(() => removeLoading(loading));
    };

    const descricaoLinha = (l: RntrcFilaPainelPayload) =>
      "a linha da fila de " +
      useRntrcFilaService()
        .getNomeFila(l.fila)
        .toLowerCase() +
      (l.idRntrcSolicitacao
        ? " da solicitação nº " + l.idRntrcSolicitacao
        : "");

    const reprocessar = (l: RntrcFilaPainelPayload) => {
      useConfirm().require({
        message: "Reprocessar " + descricaoLinha(l) + "?",
        header: "Reprocessar",
        icon: "pi pi-info-circle",
        accept: () => {
          const { loading, reprocessar } = useRntrcFilaService();
          state.loadings.push(loading);
          reprocessar(l.fila, l.idFila)
            .then(() => {
              removeLoading(loading);
              useToast().success("Linha recolocada na fila.");
              submitSearch();
            })
            .catch(() => removeLoading(loading));
        }
      });
    };

    const remover = (l: RntrcFilaPainelPayload) => {
      useConfirm().require({
        message:
          "Remover " +
          descricaoLinha(l) +
          "? O robô deixa de processá-la; a solicitação não muda de situação.",
        header: "Remover da fila",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        accept: () => {
          const { loading, remover } = useRntrcFilaService();
          state.loadings.push(loading);
          remover(l.fila, l.idFila)
            .then(() => {
              removeLoading(loading);
              useToast().success("Linha removida da fila.");
              submitSearch();
            })
            .catch(() => removeLoading(loading));
        }
      });
    };

    const resumir = (texto?: string | null) => {
      if (!texto) {
        return "";
      }
      return texto.length > 80 ? texto.substring(0, 80) + "…" : texto;
    };

    onMounted(() => {
      submitSearch();
    });

    return {
      stateLoading,
      submitSearch,
      reprocessar,
      remover,
      resumir,
      getNomeFila: useRntrcFilaService().getNomeFila,
      formatDateTime,
      formatCpfCnpj,
      getSituacaoCor,
      getSituacaoLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  }
});
</script>

<style scoped lang="scss">
.rntrc-muted {
  color: $text-muted;
}

.rntrc-prioridade {
  color: $warning;
}

.rntrc-log {
  font-size: $font-size-xs;
  color: $danger;
  word-break: break-word;
}
</style>
